import React from 'react';
import { fireEvent, render, screen } from '@testing-library/react';

import ConfigProvider from 'components/ConfigProvider';

import UserDetailsStep from './UserDetailsStep';
import { DETAILS_STEP } from './copy';

const DRAFT = {
  dmsId: '',
  email: '',
  firstName: '',
  jobRole: '',
  lastName: '',
  mobile: '',
  mobileCountryCode: '1',
};

const onBlurEmail = jest.fn();
const onBlurMobile = jest.fn();
const onChange = jest.fn();

const renderStep = (props = {}) =>
  render(
    <ConfigProvider>
      <UserDetailsStep
        draft={DRAFT}
        jobRoleOptions={[{ id: 'fleet_manager', label: 'Fleet Manager' }]}
        phoneCountryCodeOptions={[{ id: '1', label: '+1' }]}
        onBlurEmail={onBlurEmail}
        onBlurMobile={onBlurMobile}
        onChange={onChange}
        {...props}
      />
    </ConfigProvider>,
  );

beforeEach(() => {
  onBlurEmail.mockClear();
  onBlurMobile.mockClear();
  onChange.mockClear();
});

describe('management/Users UserDetailsStep', () => {
  it('renders the fields the design asks for', () => {
    renderStep();

    [
      DETAILS_STEP.firstName,
      DETAILS_STEP.lastName,
      DETAILS_STEP.jobRole,
      DETAILS_STEP.dmsId,
      DETAILS_STEP.email,
      DETAILS_STEP.countryCode,
      DETAILS_STEP.mobile,
    ].forEach((label) =>
      expect(screen.getByLabelText(label)).toBeInTheDocument(),
    );
  });

  // The design collects no username, no GPS fields and no external visibility,
  // even though the ticket's prose lists them. The username is derived server side.
  it('does not ask for a username', () => {
    renderStep();

    expect(screen.queryByLabelText(/username/i)).not.toBeInTheDocument();
  });

  it("carries the design's hints", () => {
    renderStep();

    expect(screen.getByText(DETAILS_STEP.jobRoleHelp)).toBeInTheDocument();
    expect(screen.getByText(DETAILS_STEP.dmsIdHelp)).toBeInTheDocument();
    expect(screen.getByText(DETAILS_STEP.mobileHelp)).toBeInTheDocument();
  });

  it('reports a typed field', () => {
    renderStep();

    fireEvent.change(screen.getByLabelText(DETAILS_STEP.firstName), {
      target: { value: 'Jane' },
    });

    expect(onChange).toHaveBeenCalledWith('firstName', 'Jane');
  });

  it('checks the email as the admin leaves it', () => {
    renderStep();

    fireEvent.blur(screen.getByLabelText(DETAILS_STEP.email));

    expect(onBlurEmail).toHaveBeenCalled();
  });

  it('checks the mobile as the admin leaves it', () => {
    renderStep();

    fireEvent.blur(screen.getByLabelText(DETAILS_STEP.mobile));

    expect(onBlurMobile).toHaveBeenCalled();
  });

  describe('duplicate warnings', () => {
    it('warns about a taken email', () => {
      renderStep({
        checks: { emailTaken: true, mobileTaken: false, mobileValid: true },
      });

      expect(screen.getByText(DETAILS_STEP.emailTaken)).toBeInTheDocument();
    });

    it('warns about a taken phone number', () => {
      renderStep({
        checks: { emailTaken: false, mobileTaken: true, mobileValid: true },
      });

      expect(screen.getByText(DETAILS_STEP.mobileTaken)).toBeInTheDocument();
    });

    // The warning replaces the hint rather than stacking under it, as in the
    // mockup.
    it('drops the mobile hint while warning', () => {
      renderStep({
        checks: { emailTaken: false, mobileTaken: true, mobileValid: true },
      });

      expect(
        screen.queryByText(DETAILS_STEP.mobileHelp),
      ).not.toBeInTheDocument();
    });

    it('shows nothing when neither is taken', () => {
      renderStep();

      expect(
        screen.queryByText(DETAILS_STEP.emailTaken),
      ).not.toBeInTheDocument();
      expect(
        screen.queryByText(DETAILS_STEP.mobileTaken),
      ).not.toBeInTheDocument();
    });

    // Amber on both halves of the pair, as the mockup draws it: the country code
    // is as likely to be the wrong part as the number.
    it('marks the country code as well as the number', () => {
      renderStep({
        checks: { emailTaken: false, mobileTaken: true, mobileValid: true },
      });

      // The warning border is InputField's, so what is checkable here is that the
      // Select was told about it at all — jsdom computes no styles.
      expect(
        screen.getByLabelText(DETAILS_STEP.countryCode),
      ).toBeInTheDocument();
      expect(screen.getByText(DETAILS_STEP.mobileTaken)).toBeInTheDocument();
    });
  });

  // The mockup puts both at x=0 spanning the full row (Figma 787:76709,
  // 787:76731). Hanging them off the number field's own helpMessage indented them
  // 135px, under that column alone.
  describe('the phone row message', () => {
    /**
     * True when `message` belongs to the whole row rather than to one column.
     *
     * Walks up from the message to the first ancestor holding the number, and asks
     * whether that ancestor holds the country code too. Inside the number's own
     * column it would not — which is precisely the layout being guarded against.
     */
    const spansTheRow = (message) => {
      const number = screen.getByLabelText(DETAILS_STEP.mobile);
      const code = screen.getByLabelText(DETAILS_STEP.countryCode);

      let node = message;
      while (node && !node.contains(number)) node = node.parentElement;

      return Boolean(node && node.contains(code));
    };

    it('sits beneath the whole row, not under the number', () => {
      renderStep();

      expect(spansTheRow(screen.getByText(DETAILS_STEP.mobileHelp))).toBe(true);
    });

    it('spans the row when warning too', () => {
      renderStep({
        checks: { emailTaken: false, mobileTaken: true, mobileValid: true },
      });

      expect(spansTheRow(screen.getByText(DETAILS_STEP.mobileTaken))).toBe(true);
    });
  });
});
